import { Component, OnDestroy, OnInit } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, takeUntil, timeout } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../service/auth.service';
import { TenantConfigService } from '../service/tenantConfig.service';
import { GeoLocationService } from '../geo-location.service';
import { AppConstants } from '../AppConstants';

interface Tenant {

  id: string | number;

  tenantName: string;

  status?: string;

  address?: {

    addressLine1?: string;

    city?: string;
  };

  tenantImage?: {

    totalImageCount?: number;

    [key: string]: any;
  };

  googleMapsLink?: string;

  distanceInKm?: number;
}

@Component({
  selector: 'app-products-all',
  templateUrl: './products-all.component.html',
  styleUrls: ['./products-all.component.css']
})
export class ProductsAllComponent implements OnInit, OnDestroy {

  private readonly destroyed$ = new Subject<void>();
  loadError = '';

  rowData: Tenant[] = [];

  tenantServiceIcons: {
    [tenantId: number]: string[];
  } = {};

  filteredTenants: Tenant[] = [];

  isLoading = true;

  searchText = '';

  tenantImagesObj: {
    [tenantId: string]:
    { image: string; thumbImage: string }[]
  } = {};

  constructor(
    private authService: AuthService,
    private tenantConfigService: TenantConfigService,
    private geoLocationService: GeoLocationService,
    private router: Router
  ) {}

  ngOnInit(): void {

    this.tenantConfigService.load().pipe(
      timeout(8000),
      catchError(() => of(false)),
      takeUntil(this.destroyed$)
    ).subscribe(() => {

        this.getAllTenants();

      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  getAllTenants(): void {

    this.isLoading = true;
    this.loadError = '';

    this.authService.getAlltenants().pipe(
      timeout(20000),
      takeUntil(this.destroyed$)
    ).subscribe({

      next: (res: Tenant[]) => {

        const all = Array.isArray(res) ? res : [];

        const activeTenants = all.filter(

          tenant =>

            (tenant.status || '').toLowerCase() === 'active'

        );

        //---------------------------------------
        // Sequence & City ordering
        //---------------------------------------

        const orderedTenants =
          this.tenantConfigService.sortTenants(activeTenants);

        // Location is optional: browsing must not wait for a permission prompt or GPS.
        this.setTenantRows(orderedTenants);

        if (orderedTenants.length > 0 && this.isEndUser()) {
          this.sortEndUserTenantsByLocation(orderedTenants);
        }

      },

      error: err => {

        console.error('getAllTenants error:', err);
        this.loadError = 'Unable to load venues. Please try again.';
        this.isLoading = false;

      }

    });

  }

  private isEndUser(): boolean {

    this.authService.syncUserContext();

    return AppConstants.SESSION_STORAGE_ROLEID === 3;
  }

  private sortEndUserTenantsByLocation(tenants: Tenant[]): void {

    this.geoLocationService.getCurrentLocation().pipe(
      timeout(10000),
      takeUntil(this.destroyed$)
    ).subscribe({

      next: location => {

        const sortedTenants = tenants
          .map((tenant, index) => {

            const coordinates =
              this.extractCoordinatesFromGoogleMapsLink(
                tenant.googleMapsLink
              );

            return {
              ...tenant,
              originalIndex: index,
              distanceInKm: coordinates
                ? this.calculateDistanceInKm(
                    location.latitude,
                    location.longitude,
                    coordinates.latitude,
                    coordinates.longitude
                  )
                : Number.MAX_SAFE_INTEGER
            };
          })
          .sort((a, b) => {

            if (a.distanceInKm !== b.distanceInKm) {
              return a.distanceInKm - b.distanceInKm;
            }

            return a.originalIndex - b.originalIndex;
          });

        this.setTenantRows(sortedTenants);
      },

      error: error => {

        console.warn('Unable to sort venues by user location', error);
        this.setTenantRows(tenants);
      }
    });
  }

  private setTenantRows(tenants: Tenant[]): void {

    this.rowData = tenants;
    this.filterTenants();
    this.isLoading = false;

    this.rowData.forEach(tenant => {

      this.tenantImagesObj[tenant.id] =
        this.extractTenantImages(tenant);

      this.tenantServiceIcons[tenant.id] =
        this.tenantConfigService.getTenantIcons(Number(tenant.id));

    });
  }

  private extractCoordinatesFromGoogleMapsLink(
    googleMapsLink?: string
  ): { latitude: number; longitude: number } | null {

    if (!googleMapsLink) {
      return null;
    }

    let decodedLink: string;
    try {
      decodedLink = decodeURIComponent(googleMapsLink);
    } catch {
      return null;
    }

    const patterns = [
      /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
      /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
      /[?&](?:query|q|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/
    ];

    for (const pattern of patterns) {

      const match = decodedLink.match(pattern);

      if (match) {

        const latitude = Number(match[1]);
        const longitude = Number(match[2]);

        if (
          Number.isFinite(latitude) &&
          Number.isFinite(longitude)
        ) {
          return { latitude, longitude };
        }
      }
    }

    return null;
  }

  private calculateDistanceInKm(
    userLatitude: number,
    userLongitude: number,
    tenantLatitude: number,
    tenantLongitude: number
  ): number {

    const earthRadiusKm = 6371;
    const latitudeDistance =
      this.toRadians(tenantLatitude - userLatitude);
    const longitudeDistance =
      this.toRadians(tenantLongitude - userLongitude);

    const userLatitudeRadians =
      this.toRadians(userLatitude);
    const tenantLatitudeRadians =
      this.toRadians(tenantLatitude);

    const a =
      Math.sin(latitudeDistance / 2) *
      Math.sin(latitudeDistance / 2) +
      Math.cos(userLatitudeRadians) *
      Math.cos(tenantLatitudeRadians) *
      Math.sin(longitudeDistance / 2) *
      Math.sin(longitudeDistance / 2);

    return earthRadiusKm *
      2 *
      Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  private toRadians(degrees: number): number {

    return degrees * Math.PI / 180;
  }
  filterTenants(): void {

    const search = this.searchText
      .toLowerCase()
      .trim();

    if (!search) {

      this.filteredTenants = [...this.rowData];

      return;
    }

    this.filteredTenants = this.rowData.filter(tenant => {

      const tenantName =
        tenant.tenantName?.toLowerCase() || '';

      const city =
        tenant.address?.city?.toLowerCase() || '';

      const address =
        tenant.address?.addressLine1?.toLowerCase() || '';

      return (
        tenantName.includes(search) ||
        city.includes(search) ||
        address.includes(search)
      );
    });
  }

  extractTenantImages(tenant: Tenant):
  { image: string; thumbImage: string }[] {

    const images:
      { image: string; thumbImage: string }[] = [];

    if (tenant.tenantImage?.totalImageCount) {

      const tempImages: string[] = [];

      for (
        let i = 1;
        i <= tenant.tenantImage.totalImageCount;
        i++
      ) {

        const key = `image${i}`;

        if (tenant.tenantImage[key]) {
          tempImages.push(tenant.tenantImage[key]);
        }
      }

      const defaultIndex =
        tenant.tenantImage.defaultImageIndex ?? 0;

      const sortedImages = [
        tempImages[defaultIndex],
        ...tempImages.filter((_, idx) => idx !== defaultIndex)
      ];

      sortedImages.forEach(img => {
        images.push({
          image: img,
          thumbImage: img
        });
      });
    }
    if (images.length === 0) {

      images.push({

        image: "../assets/img/noImage.png",

        thumbImage: "../assets/img/noImage.png"
      });
    }

    return images;
  }

  productDetails(id: string | number): void {

    this.router.navigate(['/product-list', id]);
  }
}


/*
import { Component, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { UIRoutes } from '../UIRoutes';
import { NgImageSliderComponent } from 'ng-image-slider';
import { AuthService } from '../service/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GeoLocationService } from '../geo-location.service';

interface TenantImageItemObj {
  image: string;
  thumbImage?: string;
  alt?: string;
  title?: string;
}
type TenantImageItem = string | TenantImageItemObj;

interface Tenant {
  id: string | number;
  tenantName: string;
  status?: string;
  address?: TenantAddress;
  tenantImage?: {
    totalImageCount?: number;
    images?: TenantImageItem[];
    [key: string]: any;
  };
  phoneNumbers?: string[];
  contactPhoneNumber1?: string;
  contactPhoneNumber2?: string;
}

interface TenantAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  pinCode?: string;
  country?: string;
}

interface CityGroup {
  city: string;
  tenants: Tenant[];
}

@Component({
  selector: 'app-products-all',
  templateUrl: './products-all.component.html',
  styleUrls: ['./products-all.component.css']
})
export class ProductsAllComponent implements OnInit {
  rowData: Tenant[] = [];
  groupedByCity: CityGroup[] = [];
  tenantImagesObj: { [tenantId: string]: { image: string; thumbImage: string }[] } = {};

  @ViewChild('slider', { static: false }) slider: NgImageSliderComponent;
  modalReference: any;
  private content: TemplateRef<any>;

  latitude: number | null = null;
  longitude: number | null = null;
  errorMessage: string = '';

  constructor(
    private modalService: NgbModal,
    private authService: AuthService,
    private router: Router,
    private geoLocationService: GeoLocationService
  ) {}

  ngOnInit(): void {
    this.getAllTenants();
    this.getGeoLocation();
  }

  private getCity(tenant: Tenant): string {
    return (tenant?.address?.city ?? 'Other').toString().trim();
  }

  getAllTenants(): void {
    this.authService.getAlltenants().subscribe({
      next: (res: Tenant[]) => {
        const all = Array.isArray(res) ? res : [];
        const visible = all.filter(t => (t.status || '').toLowerCase() === 'active');
        //const visible = all;
        visible.sort((a, b) => {
          const ca = this.getCity(a).toLowerCase();
          const cb = this.getCity(b).toLowerCase();
          return cb.localeCompare(ca);
        });

        this.rowData = visible;

        const map = new Map<string, Tenant[]>();
        for (const tenant of visible) {
          const city = this.getCity(tenant) || 'Other';
          if (!map.has(city)) map.set(city, []);
          map.get(city)!.push(tenant);

          // ✅ Cache images per tenant to avoid change detection issues
          this.tenantImagesObj[tenant.id] = this.extractTenantImages(tenant);
        }

        this.groupedByCity = Array.from(map.entries()).map(([city, tenants]) => ({
          city,
          tenants: tenants.sort((x, y) => (x.tenantName || '').localeCompare(y.tenantName || ''))
        }));
      },
      error: (err) => {
        console.error('getAllTenants error:', err);
        this.groupedByCity = [];
      }
    });
  }

  extractTenantImages(tenant: Tenant): { image: string; thumbImage: string }[] {
    const images: { image: string; thumbImage: string }[] = [];
    if (tenant.tenantImage?.totalImageCount) {
      for (let i = 1; i <= tenant.tenantImage.totalImageCount; i++) {
        const key = `image${i}`;
        if (tenant.tenantImage[key]) {
          images.push({
            image: tenant.tenantImage[key],
            thumbImage: tenant.tenantImage[key]
          });
        }
      }
    }

    if (images.length === 0) {
      images.push({
        image: "../assets/img/noImage.png",
        thumbImage: "../assets/img/noImage.png"
      });
    }

    return images;
  }

  getAllPhoneNumbers(tenant: Tenant): string {
    let phoneNumbers: string = '';
    if (tenant.contactPhoneNumber1) {
      phoneNumbers = tenant.contactPhoneNumber1;
    }
    if (tenant.contactPhoneNumber2) {
      phoneNumbers += " / " + tenant.contactPhoneNumber2;
    }
    return phoneNumbers;
  }

  productDetails(id: string | number): void {
    this.router.navigate(['/product-list', id]);
  }

  getGeoLocation(): void {
    this.geoLocationService.getCurrentLocation().subscribe({
      next: (coords) => {
        this.latitude = coords.latitude;
        this.longitude = coords.longitude;
        console.log('Latitude:', this.latitude);
        console.log('Longitude:', this.longitude);
      },
      error: (error) => {
        console.error(error);
        this.errorMessage = error;
      }
    });
  }

  displayLoginModalPopup() {
    this.modalReference = this.modalService.open(this.content, {
      windowClass: 'bottom-container',
      ariaLabelledBy: 'modal-basic-title'
    });
  }
}

 */
